import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Dispatch, SetStateAction, ReactNode } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: ReactNode; // Add optional icon support
}

interface GenericSelectProps {
  value?: string | number;
  onChange: Dispatch<SetStateAction<any>>;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "default";
  clearable?: boolean;
}

export const GenericSelect = ({
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
  size = "default",
  clearable = false,
}: GenericSelectProps) => {
  const { t } = useTranslation("shared");
  return (
    <div className="relative">
      <Select
        value={value !== undefined && value !== "" ? value.toString() : ""}
        onValueChange={(val) => {
          const originalValue = options.find((opt) => opt.value.toString() === val)?.value;
          onChange(originalValue !== undefined ? originalValue : val);
        }}
        disabled={disabled}
      >
        <SelectTrigger className={cn("h-full bg-white", className)} size={size}>
          <SelectValue placeholder={placeholder || t("genericSelect.placeholder")}>
            {value !== undefined && value !== "" && (
              <div className="flex items-center gap-2">
                {options.find((opt) => opt.value.toString() === value.toString())?.icon && (
                  <span className="flex-shrink-0">
                    {options.find((opt) => opt.value.toString() === value.toString())?.icon}
                  </span>
                )}
                <span>
                  {options.find((opt) => opt.value.toString() === value.toString())?.label}
                </span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value.toString()}
              value={option.value.toString()}
              disabled={option.disabled}
            >
              <div className="flex items-center gap-2">
                {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                <span>{option.label}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {clearable && value !== undefined && value !== "" && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange(undefined);
          }}
          className="absolute top-1/2 right-8 -translate-y-1/2 rounded-sm p-1 transition-colors hover:bg-gray-100"
          aria-label={t("genericSelect.clear")}
        >
          <X className="h-3 w-3 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  );
};

export default GenericSelect;
